import { Text } from '@mantine/core'
import {
  type CellContext,
  type Column,
  type ColumnPinningPosition,
  type PaginationState,
  type TableOptions,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import cx from 'classnames'
import { type CSSProperties, type default as React, useCallback, useEffect, useRef, useState } from 'react'
import Select from '#/components/common/Select'
import Button from '#/components/common/button'
import LoadingSpinner from '#/components/common/loadingSpinner'
import { useViewportSize } from '#/hooks/useViewportSize'
import styles from './UniversalTableCore.module.scss'

export interface UniversalTableColumn<DataItem> {
  /**
   * Pairs to data object properties. It is using dot notation, so it's possible
   * to match data from a nested object :ok:.
   */
  key: string
  /**
   * Most of the times this would be just a string, but we are open to
   * anything really.
   */
  label: React.ReactNode
  isPinned?: ColumnPinningPosition
  /**
   * This is override for the default width of a column. Use it if you need more
   * space for your data, or if you display something very short.
   *
   * When `grow` is true, this acts as the column's minimum width and any spare
   * horizontal space in the container is added on top of it.
   */
  size?: number
  /**
   * When `true`, the column expands to fill any spare horizontal space left in
   * the table container. If multiple columns set `grow`, the extra space is
   * shared between them equally. Pinned columns ignore this flag.
   *
   * The user can still resize a grow column with the column resizer; doing so
   * sets a new base width and the column will keep flexing on top of it.
   */
  grow?: boolean
  /**
   * This is an optional formatter function that will be used when rendering
   * the cell value. Without it a literal text value will be rendered. For more
   * flexibility, function receives whole original data object.
   */
  cellFormatter?: (value: DataItem, rowIndex: number) => React.ReactNode
}

interface UniversalTableProps<DataItem> {
  /** A list of column definitions */
  columns: Array<UniversalTableColumn<DataItem>>
  data: DataItem[]
  /**
   * When set to `true`, a spinner with overlay will be displayed over the table
   * rows.
   */
  isSpinnerVisible?: boolean
  /** Maximum height of the table container. Setting this enables internal scrolling. */
  maxHeight?: number | string
  /**
   * To display contextual empty message when zero rows. Falls back to a generic message, so a table never renders as
   * a bare header. Suppressed while the spinner is up, because "nothing here" would be a lie mid-fetch.
   */
  emptyMessage?: React.ReactNode
  /** Optional component to render inside a tfoot row at the bottom of the table. Useful for infinite scroll triggers. */
  bottomContent?: React.ReactNode
  // PAGINATION
  // To see footer with pagination you need to pass all these below:
  /** Starts with `0` */
  pageIndex?: number
  /** Total number of pages of data. */
  pageCount?: number
  /**
   * One of `pageSizeOptions`. It is de facto the `limit` from the `offset` and
   * `limit` pair used for paginating the endpoint.
   */
  pageSize?: number
  pageSizeOptions?: number[]
  /**
   * A way for the table to say "user wants to change pagination". It's being
   * triggered for both page navigation and page size changes.
   *
   * Provides an object with current `pageIndex` and `pageSize` (one or both
   * values are new). The second object shows previous pagination, use it to
   * compare and understand what has happened :)
   */
  onRequestPaginationChange?: (newPageInfo: PaginationState, oldPageInfo: PaginationState) => void
  // ENDPAGINATION
}

const DEFAULT_COLUMN_SIZE = {
  size: 200, // starting column size
  minSize: 60, // enforced during column resizing
  maxSize: 600, // enforced during column resizing
}

/**
 * This is a nice wrapper for the `@tanstack/react-table`. It uses only
 * a limited selection of all possible features, and provides consistent looks.
 *
 * You are responsible for passing column definitions (important!) and the data
 * to match these definitions (obviously). When using it, you need to also pass
 * the TS type of the data item, so it knows what to expect.
 *
 * It has column pinning (on column definition level, i.e. you need to tell it
 * which columns are pinned), and column resizing (works out of the box!).
 *
 * It has (optional) pagination. If you pass all the required props, you can
 * expect to get user pagination requests through the callback function named
 * `onRequestPaginationChange`.
 */
export default function UniversalTableCore<DataItem>(props: UniversalTableProps<DataItem>) {
  // We need table height for the resizers
  const [tableHeight, setTableHeight] = useState(0)
  const [hasHorizontalScrollbar, setHasHorizontalScrollbar] = useState(false)
  // Width of the scrollable table container, used to flex `grow` columns into
  // any spare horizontal space.
  const [containerWidth, setContainerWidth] = useState(0)
  const tableRef = useRef<HTMLTableElement>(null)
  const tableContainerRef = useRef<HTMLTableElement>(null)
  const { width, height } = useViewportSize()

  const moveCallback = useCallback(() => {
    if (tableRef.current) {
      setTableHeight(tableRef.current.clientHeight)
    }
  }, [])

  function onResizerStart() {
    document.addEventListener('mousemove', moveCallback)
    document.addEventListener('touchmove', moveCallback)
  }

  function onResizerEnd() {
    document.removeEventListener('mousemove', moveCallback)
    document.removeEventListener('touchmove', moveCallback)
  }

  function getCommonClassNames(column: Column<DataItem>) {
    const isPinned = column.getIsPinned()
    return cx({
      [styles.isPinnedLeft]: isPinned === 'left',
      [styles.isPinnedRight]: isPinned === 'right',
      [styles.isLastLeftPinnedColumn]: isPinned === 'left' && column.getIsLastColumn('left'),
      [styles.isFirstRightPinnedColumn]: isPinned === 'right' && column.getIsFirstColumn('right'),
    })
  }

  function getEffectiveSize(column: Column<DataItem>): number {
    const baseSize = column.getSize()
    // Pinned columns keep their explicit width so left/right offsets stay sane.
    if (column.getIsPinned()) return baseSize
    const meta = growMetaByKey.get(column.id)
    if (!meta?.grow || flexBonus <= 0) return baseSize
    return baseSize + flexBonus
  }

  function getCommonColumnStyles(column: Column<DataItem>): CSSProperties {
    const isPinned = column.getIsPinned()
    return {
      left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
      right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
      width: `${getEffectiveSize(column)}px`,
    }
  }

  const columns = props.columns.map((columnDef) => {
    return {
      accessorKey: columnDef.key,
      header: () => columnDef.label,
      cell: (cellProps: CellContext<DataItem, string>) => {
        if (columnDef.cellFormatter) {
          return columnDef.cellFormatter(cellProps.row.original, cellProps.row.index)
        } else {
          return cellProps.renderValue()
        }
      },
      size: columnDef.size || DEFAULT_COLUMN_SIZE.size,
    }
  })

  // Lookup of which columns flex, keyed by column id (i.e. the user-facing
  // `key` we pass through as `accessorKey`).
  const growMetaByKey = new Map(props.columns.map((columnDef) => [columnDef.key, { grow: Boolean(columnDef.grow) }]))

  // We define options as separate object to make the optional pagination truly
  // optional.
  const options: TableOptions<DataItem> = {
    columns: columns,
    data: props.data,
    getCoreRowModel: getCoreRowModel(),
    columnResizeMode: 'onChange',
    // Override default column sizing
    defaultColumn: DEFAULT_COLUMN_SIZE,
  }

  // Set separately because we set both `.columnPinning` and (sometimes)
  // `.pagination` and we need to be careful not to overwrite `.state` object.
  options.state = {}

  options.state.columnPinning = {
    left: props.columns.filter((col) => col.isPinned === 'left').map((col) => col.key),
    right: props.columns.filter((col) => col.isPinned === 'right').map((col) => col.key),
  }

  const hasPagination = Boolean(
    props.pageIndex !== undefined &&
      props.pageCount &&
      props.pageSize &&
      props.pageSizeOptions &&
      props.onRequestPaginationChange,
  )

  // Add pagination related options if needed
  if (
    hasPagination &&
    // `hasPagination` handles everything, but we need these two for TypeScript:
    props.pageSize &&
    props.pageIndex !== undefined
  ) {
    options.manualPagination = true
    options.pageCount = props.pageCount
    options.state.pagination = {
      pageSize: props.pageSize,
      pageIndex: props.pageIndex,
    }
    //update the pagination state when internal APIs mutate the pagination state
    options.onPaginationChange = (updater) => {
      // make sure updater is callable (to avoid typescript warning)
      if (typeof updater !== 'function') {
        return
      }

      // The `table` below is defined before usage, but we are sure it will be
      // there, given this is a callback function for it.

      const oldPageInfo = table.getState().pagination

      const newPageInfo = updater(oldPageInfo)

      if (props.onRequestPaginationChange) {
        props.onRequestPaginationChange(newPageInfo, oldPageInfo)
      }
    }
  }

  // Here we build the headless table that we would render below
  const table = useReactTable(options)

  const currentPageString = String(table.getState().pagination.pageIndex + 1)
  const totalPagesString = String(table.getPageCount())

  // Track container width with a ResizeObserver so flex columns react to
  // wrapping/modal/sidebar changes, not just viewport resizes.
  useEffect(() => {
    const node = tableContainerRef.current
    if (!node) return
    setContainerWidth(node.offsetWidth)
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry) setContainerWidth(entry.contentRect.width)
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  // How many non-pinned grow columns are eligible to share spare width.
  const growCount = props.columns.filter((col) => col.grow && !col.isPinned).length
  const totalColumnsSize = table.getTotalSize()
  const flexBonus =
    growCount > 0 && containerWidth > totalColumnsSize ? Math.floor((containerWidth - totalColumnsSize) / growCount) : 0
  const tableWidth = totalColumnsSize + flexBonus * growCount

  // Calculate the total width of all columns and the width of container, to
  // guess if there is a horizontal scrollbar
  useEffect(() => {
    const columnsWidth = table.getTotalSize()
    let measuredContainerWidth = Number.POSITIVE_INFINITY
    if (tableContainerRef.current) {
      measuredContainerWidth = tableContainerRef.current.offsetWidth
    }

    setHasHorizontalScrollbar(columnsWidth > measuredContainerWidth)
  }, [props, table, width, height])

  return (
    <div
      className={cx(styles.universalTableRoot, {
        [styles.hasHorizontalScrollbar]: hasHorizontalScrollbar,
      })}
    >
      <div
        className={cx(styles.tableContainer, {
          [styles.hasStickyHeader]: props.maxHeight !== undefined,
        })}
        ref={tableContainerRef}
        style={{
          maxHeight: props.maxHeight,
        }}
      >
        {props.isSpinnerVisible && (
          <div className={styles.spinnerOverlay}>
            <LoadingSpinner message={false} />
          </div>
        )}

        <table className={styles.table} style={{ width: tableWidth }} ref={tableRef}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className={cx(styles.tableHeaderCell, getCommonClassNames(header.column))}
                    style={{ ...getCommonColumnStyles(header.column) }}
                  >
                    {!header.isPlaceholder && flexRender(header.column.columnDef.header, header.getContext())}

                    {/*
                      TODO: if we ever see performance issues while resizing,
                      there is a way to fix that, see:
                      https://tanstack.com/table/latest/docs/guide/column-sizing#advanced-column-resizing-performance
                    */}
                    {/*
                      TODO: one of the resizers will not work for columns that
                      are `isLastLeftPinnedColumn` or `isFirstRightPinnedColumn`
                      and we are ok with this for now.
                    */}
                    <div
                      onDoubleClick={() => header.column.resetSize()}
                      onMouseDown={(event) => {
                        onResizerStart()
                        header.getResizeHandler()(event)
                      }}
                      onTouchStart={(event) => {
                        onResizerStart()
                        header.getResizeHandler()(event)
                      }}
                      onMouseUp={() => {
                        onResizerEnd()
                      }}
                      onTouchEnd={() => {
                        onResizerEnd()
                      }}
                      className={cx(styles.resizer, {
                        [styles.isResizing]: header.column.getIsResizing(),
                      })}
                    >
                      {header.column.getIsResizing() && (
                        <span className={styles.resizerLine} style={{ height: `${tableHeight}px` }} />
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {!props.isSpinnerVisible && table.getRowModel().rows.length === 0 && (
              <tr>
                <td className={styles.emptyMessageCell} colSpan={columns.length}>
                  <Text>{props.emptyMessage || t('There is no data to display.')}</Text>
                </td>
              </tr>
            )}

            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={cx(styles.tableCell, getCommonClassNames(cell.column))}
                    style={{ ...getCommonColumnStyles(cell.column) }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {props.bottomContent && (
            <tfoot>
              <tr>
                <td colSpan={columns.length}>{props.bottomContent}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {hasPagination && (
        <footer className={styles.tableFooter}>
          <section className={styles.pagination}>
            <Button
              type='text'
              size='s'
              onClick={() => table.firstPage()}
              isDisabled={!table.getCanPreviousPage()}
              startIcon='angle-bar-left'
            />

            <Button
              type='text'
              size='s'
              onClick={() => table.previousPage()}
              isDisabled={!table.getCanPreviousPage()}
              startIcon='angle-left'
            />

            <div
              className={styles.paginationNumbering}
              dangerouslySetInnerHTML={{
                __html: t('Page ##current_page## of ##total_pages##')
                  .replace('##current_page##', `<strong>${currentPageString}</strong>`)
                  .replace('##total_pages##', `<strong>${totalPagesString}</strong>`),
              }}
            />

            <Button
              type='text'
              size='s'
              onClick={() => table.nextPage()}
              isDisabled={!table.getCanNextPage()}
              startIcon='angle-right'
            />

            <Button
              type='text'
              size='s'
              onClick={() => table.lastPage()}
              isDisabled={!table.getCanNextPage()}
              startIcon='angle-bar-right'
            />
          </section>

          <Select
            className={styles.pageSizeSelect}
            size='xs'
            searchable={false}
            clearable={false}
            data={(props.pageSizeOptions || []).map((pageSize) => {
              return {
                value: String(pageSize),
                label: t('##number## rows').replace('##number##', String(pageSize)),
              }
            })}
            value={String(table.getState().pagination.pageSize)}
            onChange={(newSelectedOption) => {
              table.setPageSize(Number(newSelectedOption))
            }}
            comboboxProps={{ position: 'top' }}
          />
        </footer>
      )}
    </div>
  )
}
